import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Button, message } from 'antd';
// Still needed here, not only in SecurityStep: sendPhoneCode refuses to request
// an OTP for a number the API would reject, independently of the button's
// disabled state.
import { isValidPhoneNumber } from 'react-phone-number-input';
import {
  getUserInfo, sendOtpToSms, updateUserDetails, verifySmsOtp,
} from '../../actions/userProfile';
import { openModal } from '../../actions/modal';
import { captureAnalytics } from '../../actions/preload';
import isOnChrome from '../../global/browser';

import WalkthroughStepTemplate from './WalkthroughStepTemplate';
import WelcomeBear from '../../assets/img/redesign/bear_welcome@2x.png';
import {
  STEP_KEYS,
  chromeExtensionLink,
  completionChoices,
  faxNumberValue,
  getDeviceTools,
  getWalkthroughSteps,
  isSupportedWindowsDevice,
  isValidNpi,
  notificationKeys,
  stepEventName,
} from './walkthroughSteps';
import WorkspaceStep from './steps/WorkspaceStep';
import NotificationsStep from './steps/NotificationsStep';
import SecurityStep from './steps/SecurityStep';
import ToolsStep from './steps/ToolsStep';
import ProviderStep from './steps/ProviderStep';
import StartStep from './steps/StartStep';
import WalkthroughProgress from './steps/WalkthroughProgress';
import './walkthrough.scss';

// Step vocabulary and the pure helpers live in ./walkthroughSteps so the step
// modules can name a step without importing this container back.
export {
  completionChoices, getDeviceTools, getWalkthroughSteps, isSupportedWindowsDevice,
} from './walkthroughSteps';

class IntroToMedsender extends Component {
  constructor(props) {
    super(props);
    this.isSavingUserDetails = false;
    // Async callbacks (profile fetch, Chrome-extension ping, awaited saves) can
    // resolve after the user navigates away from the walkthrough, which unmounts
    // this route component. Every post-await setState goes through
    // setStateIfMounted and is gated on this flag.
    this.mounted = false;
    this.state = {
      stepIndex: 0,
      selectedFaxNumber: null,
      notificationSettings: {},
      notificationSettingsInitialized: false,
      notificationsChanged: false,
      chromeExtensionInstalled: false,
      isSavingUserDetails: false,
      phoneDraft: '',
      phoneCode: '',
      phoneStep: props.phoneNumber ? 'saved' : 'number',
      phoneBusy: false,
      phoneError: '',
      // Set once the user has tried to advance past a texted-but-unconfirmed
      // number, so the second Next press is an informed skip rather than a
      // silent one. Reset whenever the number or code changes.
      phoneSkipWarned: false,
      isProvider: props.isClinicalProvider !== false,
      npi: props.npi || '',
      // Loose null check: an absent (`undefined`) prop means "not loaded yet",
      // exactly like `null`. A strict `!== null` would treat it as already
      // answered and permanently skip the prefill below.
      providerDetailsInitialized: props.isClinicalProvider != null,
      launchDestination: props.walkthroughStartDestination || 'home',
      launchDestinationInitialized: props.walkthroughStartDestination != null,
      // Mirrors the phoneNumber prop so the derivation below stays edge-triggered
      // (fires only on the empty -> present transition) now that it lives in
      // getDerivedStateFromProps, which cannot see prevProps.
      syncedPhoneNumber: props.phoneNumber,
      profileLoadFailed: false,
      faxNumbersRefreshing: false,
      // The NPI validation message stays hidden until the user has blurred the
      // field or tried to advance, so a pristine step is not pre-yelled at.
      npiTouched: false,
    };
  }

  // Prop -> state derivations previously done with setState inside
  // componentDidUpdate. Each keeps its original guard verbatim, so behaviour is
  // unchanged; they simply apply one render earlier and no longer trip
  // react-doctor's no-did-update-set-state rule.
  static getDerivedStateFromProps(props, state) {
    const derivedState = {};

    if (props.phoneNumber !== state.syncedPhoneNumber) {
      derivedState.syncedPhoneNumber = props.phoneNumber;
      if (!state.syncedPhoneNumber && props.phoneNumber && state.phoneStep !== 'saved') {
        derivedState.phoneStep = 'saved';
      }
    }

    if (!state.providerDetailsInitialized && props.isClinicalProvider != null) {
      derivedState.isProvider = props.isClinicalProvider;
      derivedState.npi = props.npi || '';
      derivedState.providerDetailsInitialized = true;
    }

    if (!state.launchDestinationInitialized && props.walkthroughStartDestination) {
      derivedState.launchDestination = props.walkthroughStartDestination;
      derivedState.launchDestinationInitialized = true;
    }

    return Object.keys(derivedState).length ? derivedState : null;
  }

  componentDidMount() {
    this.mounted = true;

    if (this.props.impersonating) {
      this.props.history.replace('/app');
      return;
    }

    // A user who already finished setup should not be walked through it again
    // (for example by opening /app/walkthrough directly). This mirrors the entry
    // gate in Home.jsx, which sends users here only while hasCompletedWalkthrough
    // is false, so the two conditions are exact complements and cannot loop.
    // Checked on mount only: completing the walkthrough in this session flips the
    // flag, and re-checking on update would race the component's own navigation.
    if (this.props.hasCompletedWalkthrough) {
      this.props.history.replace('/app');
      return;
    }

    this.syncUserDetails();
    this.loadUserProfile();
    this.checkChromeExtensionInstallation().then((chromeExtensionInstalled) => {
      this.setStateIfMounted({ chromeExtensionInstalled });
    });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.defaultFaxNumber !== this.props.defaultFaxNumber
      || prevProps.defaultFaxNumbers !== this.props.defaultFaxNumbers
      || prevProps.userProfileLoaded !== this.props.userProfileLoaded
      || notificationKeys.some((key) => prevProps[key] !== this.props[key])
    ) {
      this.syncUserDetails();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getCurrentSteps = () => getWalkthroughSteps(
    { chrome: isOnChrome(), windows: isSupportedWindowsDevice() },
  );

  syncUserDetails = () => {
    const {
      defaultFaxNumbers: providedFaxNumbers,
      defaultFaxNumber,
      notification_fax_email: notificationFaxEmail,
      email_notification_emailed_records: emailedRecordsNotifications,
      email_notification_assigned_records: assignedRecordsNotifications,
      email_notification_emr_upload_failures: emrUploadFailureNotifications,
      failed_sent_faxes_notification: failedFaxNotifications,
      userProfileLoaded,
    } = this.props;
    const availableFaxNumbers = providedFaxNumbers || [];
    const savedFaxNumber = availableFaxNumbers.some((number) => faxNumberValue(number) === defaultFaxNumber)
      ? defaultFaxNumber
      : null;
    const selectedFaxNumber = availableFaxNumbers.some((number) => faxNumberValue(number) === this.state.selectedFaxNumber)
      ? this.state.selectedFaxNumber
      : (savedFaxNumber || faxNumberValue(availableFaxNumbers[0]) || null);
    const notificationValues = {
      notification_fax_email: notificationFaxEmail,
      email_notification_emailed_records: emailedRecordsNotifications,
      email_notification_assigned_records: assignedRecordsNotifications,
      email_notification_emr_upload_failures: emrUploadFailureNotifications,
      failed_sent_faxes_notification: failedFaxNotifications,
    };
    const notificationSettings = userProfileLoaded
      ? notificationKeys.reduce((settings, key) => ({
        ...settings,
        [key]: !!notificationValues[key],
      }), {})
      : this.state.notificationSettings;

    this.setState((previousState) => ({
      selectedFaxNumber,
      notificationSettings: !userProfileLoaded
        || previousState.notificationSettingsInitialized
        || previousState.notificationsChanged
        ? previousState.notificationSettings
        : notificationSettings,
      notificationSettingsInitialized: previousState.notificationSettingsInitialized || userProfileLoaded,
    }));
  };

  checkChromeExtensionInstallation = () => {
    if (!isOnChrome() || !window.chrome?.runtime?.sendMessage) return Promise.resolve(false);
    const ping = { message: 'ping' };
    const chromeExtId = process.env.CHROME_EXT_ID;
    return new Promise((resolve) => {
      window.chrome.runtime.sendMessage(chromeExtId, ping, (response) => {
        // Reading lastError prevents Chrome from logging an unchecked extension error.
        if (window.chrome.runtime.lastError) {
          resolve(false);
          return;
        }
        resolve(!!response?.message);
      });
    });
  };

  setStateIfMounted = (nextState) => {
    if (!this.mounted) return;
    this.setState(nextState);
  };

  // `getUserInfo` is this screen's ONLY profile-hydration owner, and every prop
  // it reads is selected out of `state.userProfile`. It previously also fired
  // `getLoginInfo()`, which hits the same GET /api/v1/users; the authenticated
  // route wrapper has already populated `auth.data` before this component
  // mounts, so that second request bought nothing and its LOGGED_IN dispatch
  // handed `auth.data.associatedFaxNumbers` a fresh array identity — which the
  // old reference watch in componentDidUpdate then read as "a fax number was
  // purchased" and answered with a third GET.
  loadUserProfile = () => {
    this.setStateIfMounted({ profileLoadFailed: false });

    // getUserInfo resolves with `{ error: true }` rather than rejecting (see
    // actions/helpers/crud), so a bare .catch would never fire. Both shapes are
    // handled: without this the profile silently never loads, the notifications
    // step renders every toggle off from an empty object, and completing it
    // PATCHes meaningless state.
    return Promise.resolve(this.props.getUserInfo())
      .then((result) => {
        if (result?.error) throw result;
      })
      .catch(() => {
        this.setStateIfMounted({ profileLoadFailed: true });
        message.error('We could not load your account settings. Please try again.');
      });
  };

  // Explicit success signal from the add-fax-number modal, handed to it as
  // `onFaxNumberCreated` (the same callback-in-modal-data contract
  // MergeRecordsConfirm's `onMerged` and ShareReferral's `onShareSuccess` use).
  // A created number is not yet in `default_fax_numbers`, so the list has to be
  // re-fetched, and the step stays blocked until it lands rather than letting the
  // user advance past a Select that has not caught up.
  refreshFaxNumbers = () => {
    this.setStateIfMounted({ faxNumbersRefreshing: true, profileLoadFailed: false });
    return Promise.resolve(this.props.getUserInfo())
      .then((result) => {
        if (result?.error) throw result;
        this.setStateIfMounted({ faxNumbersRefreshing: false });
      })
      .catch(() => this.setStateIfMounted({ faxNumbersRefreshing: false, profileLoadFailed: true }));
  };

  // Always called synchronously from a user event handler, so the component is
  // mounted by definition here; only the post-await paths need the guard.
  beginUserDetailsSave = () => {
    if (this.isSavingUserDetails) return false;
    this.isSavingUserDetails = true;
    this.setState({ isSavingUserDetails: true });
    return true;
  };

  finishUserDetailsSave = (nextState = {}) => {
    this.isSavingUserDetails = false;
    this.setStateIfMounted({ ...nextState, isSavingUserDetails: false });
  };

  handleNext = () => {
    if (this.isSavingUserDetails) return;

    const steps = this.getCurrentSteps();
    const currentStep = steps[this.state.stepIndex];
    if (currentStep === STEP_KEYS.workspace) return this.saveWorkspace();
    if (currentStep === STEP_KEYS.notifications) return this.saveNotifications();
    if (currentStep === STEP_KEYS.provider) return this.saveProviderDetails();
    if (currentStep === STEP_KEYS.security) return this.saveSecurity();

    return this.advanceStep(currentStep);
  };

  // The single forward transition for the whole wizard. Every path that moves the
  // user on — plain Next, and each of the four save-then-advance steps — goes
  // through here, so `walkthrough_step_advanced` covers the funnel with no holes
  // and composes with `walkthrough_step_back`. `extraState` carries the settling
  // state a save path needs to apply in the same update (clearing the saving
  // flag, for instance) rather than in a second setState that could interleave.
  advanceStep = (currentStep, extraState = {}) => {
    captureAnalytics('walkthrough_step_advanced', { step: stepEventName(currentStep) });
    this.setStateIfMounted((state) => ({ ...extraState, stepIndex: state.stepIndex + 1 }));
  };

  // The cell number is persisted ONLY by the OTP verify call
  // (Api::V1::VerifyPhoneNumberController#verify does the
  // `current_user.update(phone_number:)`), which is the same contract the
  // Settings modal relies on. A number that was texted but never confirmed is
  // therefore saved nowhere, so advancing straight past it would look to the
  // user like they had added their number when they had not.
  saveSecurity = async () => {
    const {
      phoneStep, phoneCode, phoneBusy, phoneSkipWarned,
    } = this.state;
    const currentStep = STEP_KEYS.security;

    // Nothing pending: no number entered, or one already verified and saved.
    if (phoneStep !== 'code') return this.advanceStep(currentStep);
    if (phoneBusy) return undefined;

    // A complete code is waiting — save it exactly as the Settings flow would,
    // then continue only if it actually verified.
    if (/^\d{6}$/.test(phoneCode)) {
      const verified = await this.verifyPhoneCode();
      if (verified) this.advanceStep(currentStep);
      return undefined;
    }

    // The step is optional, so skipping stays allowed — but say what will be
    // lost first. The second press goes through.
    if (!phoneSkipWarned) {
      this.setState({
        phoneSkipWarned: true,
        phoneError: 'Enter the 6-digit code to save this number. Press Next again to continue without saving it.',
      });
      return undefined;
    }

    return this.advanceStep(currentStep);
  };

  saveWorkspace = async () => {
    if (!this.props.userProfileLoaded || this.state.faxNumbersRefreshing) {
      message.error('Your workspace settings have not loaded yet. Please try again.');
      return;
    }

    const { defaultFaxNumbers = [] } = this.props;
    if (!defaultFaxNumbers.length) {
      // Nothing to save, but this is still a forward transition and has to be
      // reported as one: a user who owns no fax number is exactly the new-user
      // path the funnel is meant to measure, and advancing silently here left
      // them absent from it entirely.
      this.advanceStep(STEP_KEYS.workspace);
      return;
    }

    if (!this.beginUserDetailsSave()) return;
    try {
      await this.props.updateUserDetails({ caller_id: this.state.selectedFaxNumber || '' }, { rejectOnError: true });
      captureAnalytics('walkthrough_setting_saved', { setting: 'default_fax_number' });
      this.isSavingUserDetails = false;
      this.advanceStep(STEP_KEYS.workspace, { isSavingUserDetails: false });
    } catch {
      this.finishUserDetailsSave();
      message.error('Unable to update your default fax number. Please try again.');
    }
  };

  saveNotifications = async () => {
    // Hard guard, not just a disabled button: without a loaded profile
    // notificationSettings is an empty object, so saving here would PATCH a
    // meaningless payload while the user believes they configured notifications.
    if (!this.props.userProfileLoaded) {
      message.error('Your notification settings have not loaded yet. Please try again.');
      return;
    }

    if (!this.beginUserDetailsSave()) return;
    try {
      await this.props.updateUserDetails(this.state.notificationSettings, { rejectOnError: true });
      captureAnalytics('walkthrough_setting_saved', { setting: 'notifications' });
      this.isSavingUserDetails = false;
      this.advanceStep(STEP_KEYS.notifications, {
        isSavingUserDetails: false,
        notificationsChanged: false,
      });
    } catch {
      this.finishUserDetailsSave();
      message.error('Unable to save notification settings. Please try again.');
    }
  };

  saveProviderDetails = async () => {
    const { isProvider, npi } = this.state;
    if (isProvider && !isValidNpi(npi)) {
      // Reveal the inline hint instead of failing silently: this is the only
      // feedback a user gets that the step needs an NPI or the other option.
      this.setState({ npiTouched: true });
      return;
    }
    if (!this.beginUserDetailsSave()) return;

    try {
      await this.props.updateUserDetails({
        is_clinical_provider: isProvider,
        npi: isProvider ? npi : null,
      }, { rejectOnError: true });
      captureAnalytics('walkthrough_setting_saved', { setting: 'provider_details' });
      this.isSavingUserDetails = false;
      this.advanceStep(STEP_KEYS.provider, { isSavingUserDetails: false });
    } catch {
      this.finishUserDetailsSave();
      message.error('Unable to save your provider details. Please try again.');
    }
  };

  // Backtracking is instrumented like every other navigation and setting change:
  // without it the funnel shows only forward motion, so a step users repeatedly
  // return to is indistinguishable from one they pass straight through. Reports
  // the step being LEFT, matching walkthrough_step_advanced's subject.
  handlePrev = () => {
    if (this.isSavingUserDetails || this.state.stepIndex === 0) return;
    const currentStep = this.getCurrentSteps()[this.state.stepIndex];
    captureAnalytics('walkthrough_step_back', { step: stepEventName(currentStep) });
    this.setState((state) => ({ stepIndex: state.stepIndex - 1 }));
  };

  handleDefaultFaxChange = (selectedFaxNumber) => {
    if (this.isSavingUserDetails) return;
    captureAnalytics('walkthrough_setting_changed', { setting: 'default_fax_number' });
    this.setState({ selectedFaxNumber });
  };

  handleNotificationChange = (setting, event) => {
    if (this.isSavingUserDetails) return;
    captureAnalytics('walkthrough_setting_changed', { setting });
    this.setState((state) => ({
      notificationsChanged: true,
      notificationSettings: { ...state.notificationSettings, [setting]: event.target.checked },
    }));
  };

  handlePhoneChange = (phoneDraft) => {
    if (this.state.phoneBusy) return;
    this.setState({ phoneDraft: phoneDraft || '', phoneError: '', phoneSkipWarned: false });
  };

  // Clearing phoneSkipWarned on every edit is deliberate: the "press Next again
  // to continue without saving" warning applies to the code as it stood when it
  // was shown, so touching the code re-arms it.
  handlePhoneCodeChange = (event) => {
    this.setState({
      phoneCode: event.target.value.replace(/\D/g, ''),
      phoneError: '',
      phoneSkipWarned: false,
    });
  };

  sendPhoneCode = async () => {
    const { phoneDraft, phoneBusy } = this.state;
    if (phoneBusy || !isValidPhoneNumber(phoneDraft)) return;
    this.setState({ phoneBusy: true, phoneError: '' });
    captureAnalytics('walkthrough_security_phone_otp_requested', { action: 'request' });

    let result;
    try {
      result = await this.props.sendOtpToSms(phoneDraft);
    } catch (error) {
      result = { error };
    }

    if (result?.error) {
      this.setStateIfMounted({
        phoneBusy: false,
        phoneError: result.response?.data?.error || 'We could not text that number. Please try again.',
      });
      return;
    }

    this.setStateIfMounted({
      phoneBusy: false, phoneCode: '', phoneError: '', phoneSkipWarned: false, phoneStep: 'code',
    });
  };

  verifyPhoneCode = async () => {
    const { phoneCode, phoneDraft, phoneBusy } = this.state;
    if (phoneBusy || !/^\d{6}$/.test(phoneCode)) return false;
    this.setState({ phoneBusy: true, phoneError: '' });
    captureAnalytics('walkthrough_security_phone_otp_submitted', { action: 'verify' });

    let result;
    try {
      result = await this.props.verifySmsOtp(phoneCode, phoneDraft);
    } catch (error) {
      result = { error };
    }

    if (result?.error || result?.data?.message !== 'success') {
      this.setStateIfMounted({ phoneBusy: false, phoneError: 'That code didn\'t match. Please try again.' });
      return false;
    }

    this.setStateIfMounted({
      phoneBusy: false,
      phoneCode: '',
      phoneError: '',
      phoneSkipWarned: false,
      phoneStep: 'saved',
    });
    captureAnalytics('walkthrough_security_phone_saved', { action: 'verified' });
    this.props.getUserInfo();
    return true;
  };

  handleProviderChoice = (event) => {
    const isProvider = event.target.value === 'provider';
    captureAnalytics('walkthrough_setting_changed', { setting: 'provider_details' });
    this.setState((state) => ({
      isProvider,
      npi: isProvider ? state.npi : '',
      providerDetailsInitialized: true,
    }));
  };

  handleNpiBlur = () => {
    this.setState({ npiTouched: true });
  };

  handleNpiChange = (event) => {
    if (this.isSavingUserDetails) return;
    this.setState({
      npi: event.target.value.replace(/\D/g, '').slice(0, 10),
      // Mark the provider details as user-touched, exactly as handleProviderChoice
      // does. Without this a slow getUserInfo() that resolves mid-typing would
      // overwrite the NPI the user is entering with the stale saved value.
      providerDetailsInitialized: true,
    });
  };

  selectLaunchDestination = (event) => {
    const launchDestination = event.target.value;
    captureAnalytics('walkthrough_destination_selected', {
      destination: launchDestination,
      tutorial_available: true,
    });
    this.setState({ launchDestination, launchDestinationInitialized: true });
  };

  openAddFaxNumberModal = () => {
    captureAnalytics('walkthrough_fax_number_request_opened', { action: 'open' });
    this.props.openModal({
      type: 'ADD_FAX_NUMBER',
      data: { userIsOnWalkthrough: true, onFaxNumberCreated: this.refreshFaxNumbers },
    });
  };

  installChromeExtension = () => {
    captureAnalytics('walkthrough_device_tool_opened', { tool: 'chrome_extension' });
    const win = window.open(chromeExtensionLink, '_blank');
    if (win) win.opener = null;
  };

  installPrinterDriver = () => {
    captureAnalytics('walkthrough_device_tool_opened', { tool: 'printer_driver' });
    const win = window.open(process.env.DOWNLOAD_APP_LINK, '_blank');
    if (win) win.opener = null;
  };

  setWalkthroughAsComplete = async () => {
    if (this.isSavingUserDetails) return;
    const destination = completionChoices.find((choice) => choice.key === this.state.launchDestination)
      || completionChoices[0];

    if (!this.beginUserDetailsSave()) return;
    try {
      await this.props.updateUserDetails({
        has_completed_walkthrough: true,
        walkthrough_start_destination: destination.key,
      }, { rejectOnError: true });
      captureAnalytics('walkthrough_completed', {
        destination: destination.key,
        tutorial_available: true,
        $set: {
          walkthrough_start_destination: destination.key,
          has_completed_tutorial: this.props.hasCompletedTutorial,
        },
      });
      // Settle state BEFORE navigating: history.push unmounts this route
      // component, so any setState after it is a setState-on-unmounted-component.
      this.finishUserDetailsSave();
      this.props.history.push(destination.path);
      const { organizationDetails } = this.props;
      if (organizationDetails.show_multiple_organization_login_modal) {
        this.props.openModal({ type: 'SELECT_ORGANIZATION', data: organizationDetails });
      }
    } catch {
      this.finishUserDetailsSave();
      message.error('Unable to complete the walkthrough. Please try again.');
    }
  };

  renderWorkspaceStep = () => (
    <WorkspaceStep
      departments={this.props.departments}
      faxNumbers={this.props.defaultFaxNumbers}
      selectedFaxNumber={this.state.selectedFaxNumber}
      loaded={this.props.userProfileLoaded}
      refreshing={this.state.faxNumbersRefreshing}
      loadFailed={this.state.profileLoadFailed}
      isTrialUser={this.props.isTrialUser}
      isAdmin={this.props.isAdmin}
      canProvisionDefaultOrganizationFax={this.props.canProvisionDefaultOrganizationFax}
      onRetry={this.loadUserProfile}
      onSelectFaxNumber={this.handleDefaultFaxChange}
      onAddFaxNumber={this.openAddFaxNumberModal}
    />
  );

  renderNotificationsStep = () => (
    <NotificationsStep
      settings={this.state.notificationSettings}
      loaded={this.props.userProfileLoaded}
      loadFailed={this.state.profileLoadFailed}
      saving={this.state.isSavingUserDetails}
      onRetry={this.loadUserProfile}
      onChange={this.handleNotificationChange}
    />
  );

  renderToolsStep = () => (
    <ToolsStep
      tools={getDeviceTools({ chrome: isOnChrome(), windows: isSupportedWindowsDevice() })}
      chromeExtensionInstalled={this.state.chromeExtensionInstalled}
      onInstallChrome={this.installChromeExtension}
      onInstallPrinter={this.installPrinterDriver}
    />
  );

  renderSecurityStep = () => (
    <SecurityStep
      phoneStep={this.state.phoneStep}
      phoneDraft={this.state.phoneDraft}
      phoneCode={this.state.phoneCode}
      phoneError={this.state.phoneError}
      phoneBusy={this.state.phoneBusy}
      onPhoneChange={this.handlePhoneChange}
      onCodeChange={this.handlePhoneCodeChange}
      onSendCode={this.sendPhoneCode}
      onVerifyCode={this.verifyPhoneCode}
    />
  );

  renderProviderStep = () => (
    <ProviderStep
      isProvider={this.state.isProvider}
      npi={this.state.npi}
      npiTouched={this.state.npiTouched}
      onChoice={this.handleProviderChoice}
      onNpiBlur={this.handleNpiBlur}
      onNpiChange={this.handleNpiChange}
    />
  );

  renderStartStep = () => (
    <StartStep
      launchDestination={this.state.launchDestination}
      onSelect={this.selectLaunchDestination}
    />
  );

  getStepContent = (step) => {
    switch (step) {
      case STEP_KEYS.welcome:
        return <WalkthroughStepTemplate title="Welcome to Medsender" img={WelcomeBear} subtitle="Let's get your workspace ready." />;
      case STEP_KEYS.workspace:
        return this.renderWorkspaceStep();
      case STEP_KEYS.notifications:
        return this.renderNotificationsStep();
      case STEP_KEYS.security:
        return this.renderSecurityStep();
      case STEP_KEYS.tools:
        return this.renderToolsStep();
      case STEP_KEYS.provider:
        return this.renderProviderStep();
      case STEP_KEYS.start:
        return this.renderStartStep();
      default:
        return null;
    }
  };

  render() {
    const steps = this.getCurrentSteps();
    const { stepIndex, isSavingUserDetails } = this.state;
    const currentStep = steps[stepIndex];
    const onLastSlide = currentStep === STEP_KEYS.start;
    const notificationsUnavailable = currentStep === STEP_KEYS.notifications && !this.props.userProfileLoaded;
    const workspaceUnavailable = currentStep === STEP_KEYS.workspace
      && (!this.props.userProfileLoaded || this.state.faxNumbersRefreshing || this.state.profileLoadFailed);

    return (
      <div className="walkthrough-container">
        <Helmet><title>Getting Started - Medsender</title></Helmet>
        <div className="walkthrough-panel">
          <WalkthroughProgress steps={steps} stepIndex={stepIndex} />
          <div className="walkthrough-content">
            {this.getStepContent(currentStep)}
            <div className="walkthrough-actions">
              <Button type="text" disabled={stepIndex === 0 || isSavingUserDetails} onClick={this.handlePrev}>Back</Button>
              {/* Deliberately NOT disabled for an incomplete NPI: a disabled Next
                  gave no reason and left first-run users stuck. saveProviderDetails
                  refuses and surfaces the inline hint instead. */}
              <Button type="primary" disabled={isSavingUserDetails || notificationsUnavailable || workspaceUnavailable} loading={isSavingUserDetails} onClick={onLastSlide ? this.setWalkthroughAsComplete : this.handleNext}>
                {onLastSlide ? 'Continue' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

IntroToMedsender.defaultProps = {
  defaultFaxNumber: null,
  defaultFaxNumbers: [],
  departments: [],
  phoneNumber: '',
  isClinicalProvider: null,
  npi: null,
  userProfileLoaded: false,
  canProvisionDefaultOrganizationFax: true,
  walkthroughStartDestination: null,
  hasCompletedTutorial: false,
  hasCompletedWalkthrough: false,
  isAdmin: false,
};

IntroToMedsender.propTypes = {
  updateUserDetails: PropTypes.func.isRequired,
  sendOtpToSms: PropTypes.func.isRequired,
  verifySmsOtp: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  isTrialUser: PropTypes.bool.isRequired,
  organizationDetails: PropTypes.object.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  impersonating: PropTypes.bool.isRequired,
  hasCompletedWalkthrough: PropTypes.bool,
  departments: PropTypes.array,
  defaultFaxNumber: PropTypes.string,
  defaultFaxNumbers: PropTypes.array,
  isAdmin: PropTypes.bool,
  phoneNumber: PropTypes.string,
  isClinicalProvider: PropTypes.bool,
  npi: PropTypes.string,
  userProfileLoaded: PropTypes.bool,
  canProvisionDefaultOrganizationFax: PropTypes.bool,
  walkthroughStartDestination: PropTypes.string,
  hasCompletedTutorial: PropTypes.bool,
  notification_fax_email: PropTypes.bool.isRequired,
  email_notification_emailed_records: PropTypes.bool.isRequired,
  email_notification_assigned_records: PropTypes.bool.isRequired,
  email_notification_emr_upload_failures: PropTypes.bool.isRequired,
  failed_sent_faxes_notification: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
};

export default connect((state) => ({
  isTrialUser: state.auth.trialUser,
  organizationDetails: state.auth.organizationDetails,
  impersonating: state.auth.impersonating,
  hasCompletedWalkthrough: state.auth.hasCompletedWalkthrough,
  isAdmin: state.auth.isAdmin,
  departments: state.auth.departments,
  // Every fax-number fact on this screen is read out of `state.userProfile`, the
  // store `getUserInfo` hydrates, so the screen has exactly one owner for its
  // profile data. `user_fax_number` is the saved default that GET /api/v1/users
  // ships beside `default_fax_numbers` (User#fax_number_auth_fields), and is the
  // same selection components/redesign/pages/UserProfile.jsx makes. Reading it
  // from `auth.data` instead meant depending on a store this screen no longer
  // hydrates, and on the array identity of a sibling key.
  defaultFaxNumber: state.userProfile.user_fax_number,
  defaultFaxNumbers: state.userProfile.default_fax_numbers,
  phoneNumber: state.userProfile.phone_number,
  isClinicalProvider: state.userProfile.is_clinical_provider,
  npi: state.userProfile.npi,
  // SETTLED AND SUCCEEDED, not "an email exists somewhere". This gate hard-blocks
  // saveNotifications and saveWorkspace, so it has to mean the fetch landed —
  // the same distinction reducers/userProfile.js draws and LearnMedsender reads.
  userProfileLoaded: !!state.userProfile.userInfoLoaded && !state.userProfile.userInfoLoadFailed,
  // Defaults to TRUE when absent so a payload that predates this field keeps the
  // existing offer rather than silently hiding it for everyone.
  canProvisionDefaultOrganizationFax: state.userProfile.can_provision_default_organization_fax !== false,
  walkthroughStartDestination: state.userProfile.walkthrough_start_destination,
  hasCompletedTutorial: !!state.userProfile.has_completed_tutorial,
  notification_fax_email: state.userProfile.notification_fax_email,
  email_notification_emailed_records: state.userProfile.email_notification_emailed_records,
  email_notification_assigned_records: state.userProfile.email_notification_assigned_records,
  email_notification_emr_upload_failures: state.userProfile.email_notification_emr_upload_failures,
  failed_sent_faxes_notification: state.userProfile.failedSentFaxesNotification,
}), {
  updateUserDetails, openModal, getUserInfo, sendOtpToSms, verifySmsOtp,
})(IntroToMedsender);
